import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { cpdCertificatesService } from '../api/cpdCertificatesService';

function CPDCertificates() {
  const navigate = useNavigate();
  const [cpdCertificates, setCpdCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [userCanIssue, setUserCanIssue] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [formData, setFormData] = useState({
    candidate_name: '',
    training_topic: '',
    completion_date: new Date().toISOString().split('T')[0],
    cpd_hours: '',
    issuing_organization: '',
    trainer_name: '',
    assessment_results: '',
    company_id: ''
  });

  useEffect(() => {
    checkPermission();
  }, []);

  const checkPermission = async () => {
    try {
      // Get user from localStorage first
      const userRole = localStorage.getItem('userRole');
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');

      // Super admin always has access
      if (userRole === 'super_admin') {
        setCurrentUser({ ...storedUser, role: userRole });
        setUserCanIssue(true);
        fetchCPDCertificates();
        fetchCompanies();
        return;
      }

      // For other roles, check can_issue_cpd permission
      // Try to get fresh user data from API
      try {
        const response = await axiosClient.get('/auth/me');
        setCurrentUser(response.data);

        const hasPermission = response.data.can_issue_cpd || response.data.role === 'super_admin';
        setUserCanIssue(hasPermission);

        if (hasPermission) {
          fetchCPDCertificates();
          fetchCompanies();
        }
      } catch (apiErr) {
        // If /auth/me doesn't exist, fall back to localStorage
        console.log('Using localStorage for permission check');
        setCurrentUser({ ...storedUser, role: userRole });

        // Check permission from localStorage
        const hasPermission = storedUser.can_issue_cpd || userRole === 'super_admin';
        setUserCanIssue(hasPermission);

        if (hasPermission) {
          fetchCPDCertificates();
          fetchCompanies();
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Permission check failed:', err);
      setLoading(false);
    }
  };

  const fetchCPDCertificates = async () => {
    try {
      setLoading(true);
      const response = await cpdCertificatesService.list();
      setCpdCertificates(response.data);
    } catch (err) {
      console.error('Error fetching CPD certificates:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axiosClient.get('/companies');
      // Filter out third party admins, only show client companies
      setCompanies(response.data.filter(c => !c.is_third_party_admin));
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        cpd_hours: parseFloat(formData.cpd_hours),
        completion_date: new Date(formData.completion_date).toISOString(),
        company_id: formData.company_id ? parseInt(formData.company_id) : null
      };

      await cpdCertificatesService.create(payload);

      setShowCreateForm(false);
      setFormData({
        candidate_name: '',
        training_topic: '',
        completion_date: new Date().toISOString().split('T')[0],
        cpd_hours: '',
        issuing_organization: '',
        trainer_name: '',
        assessment_results: '',
        company_id: ''
      });
      fetchCPDCertificates();
      alert('CPD certificate issued successfully!');
    } catch (err) {
      console.error('Error creating CPD certificate:', err);
      alert('Failed to issue CPD certificate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this CPD certificate?')) {
      return;
    }

    try {
      await cpdCertificatesService.delete(id);
      fetchCPDCertificates();
      alert('CPD certificate deleted successfully');
    } catch (err) {
      console.error('Error deleting CPD certificate:', err);
      alert('Failed to delete CPD certificate: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDownloadPdf = async (id, certificateNumber) => {
    try {
      const response = await cpdCertificatesService.downloadPdf(id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cpd_certificate_${certificateNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error downloading PDF:', err);
      alert('Failed to download PDF: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return <div style={{ padding: '20px' }}>Loading CPD certificates...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>CPD Certificates</h1>
          <p style={{ color: '#666', margin: 0 }}>Issue and manage Continuing Professional Development certificates</p>
        </div>
        {userCanIssue && (
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {showCreateForm ? 'Cancel' : '+ Issue New CPD Certificate'}
          </button>
        )}
      </div>

      {/* Subscription Gate */}
      {!userCanIssue && !loading && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '12px',
          padding: '40px',
          color: 'white',
          textAlign: 'center',
          marginTop: '40px',
          boxShadow: '0 10px 40px rgba(102, 126, 234, 0.3)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>--</div>
          <h2 style={{ margin: '0 0 15px 0', fontSize: '28px' }}>CPD Certificate Issuing</h2>
          <p style={{ fontSize: '16px', marginBottom: '30px', opacity: 0.9, maxWidth: '600px', margin: '0 auto 30px' }}>
            Upgrade your subscription to unlock the ability to issue Continuing Professional Development (CPD) certificates to your clients.
          </p>
          <div style={{
            background: 'rgba(255, 255, 255, 0.2)',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '30px',
            maxWidth: '500px',
            margin: '0 auto 30px'
          }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '18px' }}>Features include:</h3>
            <ul style={{ textAlign: 'left', listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>Issue unlimited CPD certificates</li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>Track CPD hours and training records</li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>Professional certificate templates</li>
              <li style={{ padding: '8px 0' }}>Digital certificate management</li>
            </ul>
          </div>
          <button
            onClick={() => window.location.href = 'mailto:sales@icsc.org?subject=CPD Certificate Subscription Inquiry'}
            style={{
              padding: '15px 40px',
              background: 'white',
              color: '#667eea',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
              transition: 'transform 0.2s'
            }}
            onMouseOver={(e) => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.target.style.transform = 'scale(1)'}
          >
            Contact Sales to Upgrade
          </button>
        </div>
      )}

      {showCreateForm && userCanIssue && (
        <div style={{ backgroundColor: '#f8f9fa', padding: '20px', borderRadius: '4px', marginBottom: '20px' }}>
          <h2>Issue New CPD Certificate</h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Candidate Name *</label>
                <input
                  type="text"
                  name="candidate_name"
                  value={formData.candidate_name}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Training Topic/Course Name *</label>
                <input
                  type="text"
                  name="training_topic"
                  value={formData.training_topic}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Completion Date *</label>
                <input
                  type="date"
                  name="completion_date"
                  value={formData.completion_date}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>CPD Hours Earned *</label>
                <input
                  type="number"
                  step="0.5"
                  name="cpd_hours"
                  value={formData.cpd_hours}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Issuing Organization *</label>
                <input
                  type="text"
                  name="issuing_organization"
                  value={formData.issuing_organization}
                  onChange={handleInputChange}
                  required
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Trainer Name (Optional)</label>
                <input
                  type="text"
                  name="trainer_name"
                  value={formData.trainer_name}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Assessment Results (Optional)</label>
                <textarea
                  name="assessment_results"
                  value={formData.assessment_results}
                  onChange={handleInputChange}
                  rows="3"
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px' }}>Company (Optional)</label>
                <select
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleInputChange}
                  style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
                >
                  <option value="">Not linked to a company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
              <button
                type="submit"
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Issue CPD Certificate
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {userCanIssue && (
        <div style={{ backgroundColor: 'white', borderRadius: '4px', padding: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
          <h2>Issued CPD Certificates</h2>
          {cpdCertificates.length === 0 ? (
          <p style={{ color: '#666' }}>No CPD certificates issued yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>Certificate No</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Candidate Name</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Training Topic</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>CPD Hours</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Completion Date</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Issuing Organization</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {cpdCertificates.map(cert => (
                <tr key={cert.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '12px' }}>{cert.certificate_number}</td>
                  <td style={{ padding: '12px' }}>{cert.candidate_name}</td>
                  <td style={{ padding: '12px' }}>{cert.training_topic}</td>
                  <td style={{ padding: '12px' }}>{cert.cpd_hours}</td>
                  <td style={{ padding: '12px' }}>{new Date(cert.completion_date).toLocaleDateString()}</td>
                  <td style={{ padding: '12px' }}>{cert.issuing_organization}</td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      {cert.pdf_path && (
                        <button
                          onClick={() => handleDownloadPdf(cert.id, cert.certificate_number)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Download PDF
                        </button>
                      )}
                      {currentUser?.role === 'super_admin' && (
                        <button
                          onClick={() => handleDelete(cert.id)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                          }}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}
    </div>
  );
}

export default CPDCertificates;
